'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { calcCartShippingService, selectCartShippingService } from '@/services/domain/cart/CartShippingService'
import { getCartNotFound } from '@/services/domain/cart/cartErrors'
import { useCartStore } from '@/stores/useCartStore'
import { CartShippingFormSchema, type ICartShippingResponse } from '~/schemas/cart/cart'
import { formatZipCode, normalizeZipCode } from '~/schemas/customer/address'
import { isContractError } from '~/schemas/errorSchemas'
import type {
    ISelectShippingRequest,
    ISellerShippingQuote,
    IShippingBySeller,
    IShippingQuote,
} from '~/schemas/shared/shipping'
import { useCartCache } from './useCartCache'

/**
 * Frete do carrinho. `calculate(cep)` cota no `cart/calc-shipping-maestro` (o service grava na `useCartStore` o
 * carrinho com o frete da primeira cotação e o CEP digitado) e guarda as cotações no cache `['cart', 'shipping']`. Com
 * `autoCalculate`, recalcula ao montar quando o carrinho da montagem já tem CEP e itens (o que a tela do carrinho
 * fazia); cada `calculate` é uma cotação só.
 *
 * Os itens do carrinho mudaram (remoção, quantidade) com `autoCalculate` e CEP: recota uma vez. Cotação automática que a
 * API não faz (a montagem ou a mudança de itens) deixa o aviso fixo e `hasQuoteFailure`: o carrinho guardado ainda tem
 * o frete de antes, que não vale para os itens de agora, e a tela não o mostra como se valesse.
 *
 * Sem frete por vendedor (production/api), não há escolha de opção: a API cobra sempre a primeira cotação
 * (`appliedQuote`, preço em `cart.shippingTax`, que já tem o efeito do cupom); as outras são informativas, cada uma com o
 * próprio preço. Com frete por vendedor (staging/api, `sellers`), o cliente escolhe a opção de cada vendedor por
 * `selectShipping`: uma escolha por vez, com a opção em voo em `pendingSelection`; o service grava o carrinho e o hook
 * troca o detalhamento no cache `['cart', 'shipping']`.
 *
 * Sem "frete grátis por UF": o carrinho legado consultava a UF do CEP depois de cotar (por `fetch` relativo, na origem
 * errada) e calculava um `hasFreeShipping` que nenhuma tela exibia; a consulta saiu junto com o cálculo.
 */

export interface ICartShippingResult {
    /** CEP cotado, só dígitos. */
    zipCode: string
    quotes: IShippingQuote[]
    /** Frete por vendedor (staging/api); `null` quando a API não manda. */
    shippingBySeller: IShippingBySeller | null
}

const EMPTY: IShippingQuote[] = []

/** Os itens do carrinho como texto estável (produto e quantidade): muda quando a cotação deixa de valer. */
const selectItemsSignature = (state: ReturnType<typeof useCartStore.getState>) =>
    (state.data?.cartItems ?? [])
        .map((item) => `${item.product.id}:${item.quantity}`)
        .sort()
        .join('|')
const NO_SELLERS: ISellerShippingQuote[] = []

async function fetchCartShipping(
    zipCode: string,
): Promise<{ response: ICartShippingResponse; result: ICartShippingResult }> {
    const response = await calcCartShippingService(zipCode)
    return {
        response,
        result: {
            zipCode: normalizeZipCode(zipCode),
            quotes: response.shipmentResponse.quotes,
            shippingBySeller: response.shipmentBySeller,
        },
    }
}

export function useCartShipping({ autoCalculate = false }: { autoCalculate?: boolean } = {}) {
    const queryClient = useQueryClient()
    const { setCartCache, handleCartError } = useCartCache()
    const cartZipCode = useCartStore((state) => state.data?.cep ?? '')
    /**
     * O recálculo automático olha o carrinho da montagem (o cache persistido). Não lê o CEP reativo: o service grava o
     * carrinho com o CEP na store antes do `setQueryData` da mutation, e a query ligaria no meio do primeiro cálculo,
     * cotando duas vezes.
     */
    const [autoCalculateOnMount] = useState(() => {
        const cart = useCartStore.getState().data
        return autoCalculate && Boolean(cart?.cep) && (cart?.cartItems.length ?? 0) > 0
    })
    const shippingTax = useCartStore((state) => state.data?.shippingTax ?? 0)
    const [zipCodeError, setZipCodeError] = useState<string | null>(null)
    /** Aviso fixo de quando a API não cotou o CEP (a cotação e o CEP do carrinho continuam os de antes). */
    const [calculationError, setCalculationError] = useState<string | null>(null)
    /** A cotação automática dos itens de agora falhou: o frete guardado no carrinho não vale para eles. */
    const [quoteFailed, setQuoteFailed] = useState(false)
    const itemsSignature = useCartStore(selectItemsSignature)
    const quotedSignature = useRef(itemsSignature)
    /** Cotação em voo: o service grava na store o carrinho da resposta antes de o hook saber, e isso não é item novo. */
    const quoting = useRef(0)

    /** Cotou com sucesso: a resposta traz o carrinho dos itens cotados, que não pede outra cotação. */
    const markQuoted = useCallback(() => {
        quotedSignature.current = selectItemsSignature(useCartStore.getState())
        setQuoteFailed(false)
    }, [])

    const flagItemsQuoteFailure = useCallback((zipCode: string) => {
        setQuoteFailed(true)
        setCalculationError(
            `Não foi possível calcular o frete dos itens do carrinho para o CEP ${formatZipCode(zipCode)}. Confira os itens e tente de novo.`,
        )
    }, [])

    const query = useQuery({
        queryKey: ['cart', 'shipping'],
        queryFn: async () => {
            const zipCode = useCartStore.getState().data?.cep ?? ''
            quoting.current += 1
            try {
                const { response, result } = await fetchCartShipping(zipCode)
                setCartCache(response.cart)
                markQuoted()
                setCalculationError(null)
                return result
            } catch (error) {
                handleCartError(error)
                if (!getCartNotFound(error) && !isContractError(error)) flagItemsQuoteFailure(zipCode)
                throw error
            } finally {
                quoting.current -= 1
            }
        },
        enabled: autoCalculateOnMount,
        // O aviso fixo sai na primeira falha; repetir só repetiria o toast.
        retry: false,
    })

    const mutation = useMutation({
        mutationFn: fetchCartShipping,
        onSuccess: ({ response, result }) => {
            setCartCache(response.cart)
            queryClient.setQueryData(['cart', 'shipping'], result)
        },
    })

    /**
     * Resolve com as cotações ou `null`. CEP mal formado não chama a API e fica em `zipCodeError` (para o campo); o
     * erro da API já saiu no toast, e o `cartNotFound` troca o carrinho guardado.
     */
    const runCalculate = useCallback(
        async (zipCode: string, automatic: boolean): Promise<ICartShippingResult | null> => {
            const form = CartShippingFormSchema.safeParse({ zipCode })
            if (!form.success) {
                setZipCodeError(form.error.issues[0]?.message ?? 'CEP inválido')
                return null
            }
            setZipCodeError(null)
            setCalculationError(null)
            quoting.current += 1
            try {
                const { result } = await mutation.mutateAsync(zipCode)
                markQuoted()
                return result
            } catch (error) {
                handleCartError(error)
                // `cartNotFound` já avisou pelo toast e a resposta fora do contrato revalida o carrinho; o resto é CEP que a
                // API não cotou: a store e o cache não foram tocados, então o frete de antes continua valendo.
                if (!getCartNotFound(error) && !isContractError(error) && automatic) {
                    flagItemsQuoteFailure(zipCode)
                } else if (!getCartNotFound(error) && !isContractError(error)) {
                    const kept = queryClient.getQueryData<ICartShippingResult>(['cart', 'shipping'])?.zipCode
                    setCalculationError(
                        `Não foi possível calcular o frete para o CEP ${formatZipCode(zipCode)}. Confira o CEP e tente de novo.` +
                            (kept ? ` Mantivemos o frete do CEP ${formatZipCode(kept)}.` : ''),
                    )
                }
                return null
            } finally {
                quoting.current -= 1
            }
        },
        [mutation.mutateAsync, handleCartError, queryClient, flagItemsQuoteFailure, markQuoted],
    )

    const calculate = useCallback((zipCode: string) => runCalculate(zipCode, false), [runCalculate])

    // Os itens mudaram: o frete cotado era dos itens de antes. A montagem já cota pela query, sem passar por aqui.
    useEffect(() => {
        if (quoting.current > 0 || quotedSignature.current === itemsSignature) return
        quotedSignature.current = itemsSignature
        const zipCode = useCartStore.getState().data?.cep
        // Fora do corpo do efeito: a cotação grava estado de aviso, e isso dentro do efeito renderizaria em cascata.
        if (autoCalculate && zipCode && itemsSignature) queueMicrotask(() => void runCalculate(zipCode, true))
    }, [itemsSignature, autoCalculate, runCalculate])

    /** Escolha em voo: guarda síncrona contra o duplo clique (o `isPending` da mutation só muda no próximo render). */
    const selecting = useRef(false)

    const selectMutation = useMutation({
        mutationFn: ({ sellerId, offerCode }: ISelectShippingRequest) => selectCartShippingService(sellerId, offerCode),
        onSuccess: (response) => {
            setCartCache(response.cart)
            queryClient.setQueryData<ICartShippingResult>(['cart', 'shipping'], (current) =>
                current ? { ...current, shippingBySeller: response.shipmentBySeller } : current,
            )
        },
    })

    const { mutateAsync: calculateAsync } = mutation
    const { mutateAsync: selectAsync } = selectMutation

    /**
     * Escolhe a opção de frete de um vendedor. Resolve `true` quando a API aplicou e `false` quando não saiu (outra
     * escolha em voo) ou falhou (o toast já saiu no service; o `cartNotFound` troca o carrinho guardado). Resposta 2xx
     * fora do contrato: a escolha pode ter sido gravada, então recota o frete para mostrar o que vale.
     */
    const selectShipping = useCallback(
        async (sellerId: string, offerCode: string): Promise<boolean> => {
            if (selecting.current) return false
            selecting.current = true
            try {
                await selectAsync({ sellerId, offerCode })
                return true
            } catch (error) {
                handleCartError(error)
                const zipCode = useCartStore.getState().data?.cep
                if (isContractError(error) && zipCode) {
                    await calculateAsync(zipCode).catch(() => null)
                }
                return false
            } finally {
                selecting.current = false
            }
        },
        [selectAsync, calculateAsync, handleCartError],
    )

    const quotes = query.data?.quotes ?? EMPTY

    return {
        quotes,
        /** A cotação que a API aplicou no carrinho (sempre a primeira). */
        appliedQuote: quotes[0] ?? null,
        shippingTax,
        zipCode: query.data?.zipCode ?? cartZipCode,
        zipCodeError,
        calculationError,
        /** A API não cotou o frete dos itens de agora: o frete do carrinho não vale e o resumo não o soma. */
        hasQuoteFailure: quoteFailed,
        calculate,
        isCalculating: mutation.isPending || query.isFetching,
        /** Vendedores com as opções de frete (staging/api); vazio quando a API não manda o detalhamento. */
        sellers: query.data?.shippingBySeller?.sellers ?? NO_SELLERS,
        selectShipping,
        /** Opção em voo, ou `null`. */
        pendingSelection: selectMutation.isPending ? (selectMutation.variables ?? null) : null,
        isSelecting: selectMutation.isPending,
    }
}
